/**
 * IndexedDB-backed deep history cache for Conversations v2 (iter136.7).
 *
 * Why IDB and not localStorage?
 * ─────────────────────────────
 * The synchronous ``convMessageCache.js`` (localStorage) stays in
 * place as the instant-paint cache — capped at 50 messages per
 * conversation so it fits in the 5 MB origin budget alongside
 * portal state. That's enough for a fast first frame but useless
 * for scroll-up history loads beyond the most recent page.
 *
 * IDB lifts the cap to ~500 messages per conv across hundreds of
 * conversations, which means once a user has paged through their
 * thread once, scroll-up is purely client-local — zero round-trips
 * to the remote MySQL host until they hit messages they've never
 * seen before.
 *
 * Storage shape
 * ─────────────
 *   DB:    ``usair_conv_msg_cache`` v1
 *   Store: ``messages`` — keyPath ``[conversation_id, sequence_number]``
 *          Index ``by_conv_seq`` on the same composite, for range scans.
 *
 * Eviction policy (decided in iter136.7 design):
 *   • Soft cap of 500 messages per conversation (oldest evicted first).
 *   • Hard floor of 30 days — anything older is swept out on init.
 *   • Whichever fires first wins.
 *
 * Invalidation:
 *   • WS ``message_edited`` → ``patchOne`` updates the row in-place.
 *   • WS ``message_recalled`` → ``patchOne({recalled:true, content:""})``.
 *   • WS ``message_new`` → ``putOne`` upserts.
 *   • Logout → ``clearAll`` drops the entire database.
 *
 * The module is BEST-EFFORT. Every method silently swallows
 * exceptions so a missing IDB (private mode, Safari quota,
 * Firefox permanent denial) never breaks the conversation UI.
 */

const DB_NAME = "usair_conv_msg_cache";
const DB_VERSION = 1;
const STORE = "messages";
const PER_CONV_SOFT_CAP = 500;
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;  // 30 days

let _dbPromise = null;
let _disabled = false;

function _open() {
  if (_disabled) return Promise.resolve(null);
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === "undefined") {
      _disabled = true;
      resolve(null);
      return;
    }
    let req;
    try { req = indexedDB.open(DB_NAME, DB_VERSION); }
    catch (_e) { _disabled = true; resolve(null); return; }
    req.onerror = () => { _disabled = true; resolve(null); };
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, {
          keyPath: ["conversation_id", "sequence_number"],
        });
        // The composite key is its own index; we still create
        // a named index for clarity and forward-compat.
        store.createIndex(
          "by_conv_seq",
          ["conversation_id", "sequence_number"],
          { unique: true },
        );
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
  return _dbPromise;
}

function _tx(db, mode) {
  return db.transaction(STORE, mode).objectStore(STORE);
}

function _toRow(convId, m) {
  // Strip optimistic-only fields — IDB rows must reflect the
  // server-confirmed shape so scroll-up cache hits don't surface
  // ghost messages with placeholder ``Number.MAX_SAFE_INTEGER`` seqs.
  if (!m || !m.message_id || !Number.isFinite(m.sequence_number)) return null;
  if (m._optimistic || m._failed) return null;
  return {
    conversation_id: convId,
    sequence_number: m.sequence_number,
    message_id: m.message_id,
    sender: m.sender || null,
    content_type: m.content_type || "text",
    content: m.content || "",
    server_timestamp: m.server_timestamp || null,
    edited: !!m.edited,
    recalled: !!m.recalled,
    event_type: m.event_type || null,
    event_subject: m.event_subject || null,
    event_subject_label: m.event_subject_label || null,
    mentions: Array.isArray(m.mentions) ? m.mentions : [],
    cached_at: Date.now(),
  };
}

// ── Public API (all async, all soft-fail) ───────────────────────

/** Bulk upsert messages for a conversation. */
export async function putAll(convId, messages) {
  if (!convId || !messages || messages.length === 0) return;
  const db = await _open(); if (!db) return;
  try {
    await new Promise((resolve) => {
      const store = _tx(db, "readwrite");
      for (const m of messages) {
        const row = _toRow(convId, m);
        if (row) store.put(row);
      }
      store.transaction.oncomplete = resolve;
      store.transaction.onerror = resolve;
      store.transaction.onabort = resolve;
    });
    // Fire-and-forget eviction sweep — we don't block writes on it.
    _enforceSoftCap(convId).catch(() => {});
  } catch (_e) { /* swallow */ }
}

/** Upsert a single message — used by WS ``message_new``. */
export async function putOne(convId, message) {
  if (!convId) return;
  return putAll(convId, [message]);
}

/** Patch a cached message in-place (edit / recall). */
export async function patchOne(convId, messageId, patch) {
  if (!convId || !messageId || !patch) return;
  const db = await _open(); if (!db) return;
  try {
    await new Promise((resolve) => {
      const store = _tx(db, "readwrite");
      // Without sequence_number we can't address the row directly —
      // fall back to a cursor scan within the conversation partition.
      const range = IDBKeyRange.bound(
        [convId, -Infinity], [convId, Infinity],
      );
      const req = store.openCursor(range);
      req.onsuccess = (ev) => {
        const cursor = ev.target.result;
        if (!cursor) { resolve(); return; }
        if (cursor.value && cursor.value.message_id === messageId) {
          const next = { ...cursor.value, ...patch };
          cursor.update(next);
          resolve();
          return;
        }
        cursor.continue();
      };
      req.onerror = resolve;
    });
  } catch (_e) { /* swallow */ }
}

/** Read messages older than ``beforeSeq`` for ``convId`` (descending,
 *  newest of the older slice first), capped at ``limit``. Returns
 *  them re-sorted ascending so the caller can prependMessages
 *  directly. Empty array on miss / disabled / quota.
 */
export async function getOlderRange(convId, { beforeSeq, limit = 50 }) {
  if (!convId || !Number.isFinite(beforeSeq)) return [];
  const db = await _open(); if (!db) return [];
  try {
    const items = await new Promise((resolve) => {
      const store = _tx(db, "readonly");
      const range = IDBKeyRange.bound(
        [convId, -Infinity], [convId, beforeSeq - 1],
      );
      const out = [];
      // ``prev`` walks the index in descending key order.
      const req = store.openCursor(range, "prev");
      req.onsuccess = (ev) => {
        const cursor = ev.target.result;
        if (!cursor || out.length >= limit) { resolve(out); return; }
        out.push(cursor.value);
        cursor.continue();
      };
      req.onerror = () => resolve([]);
    });
    // Caller wants chronological order.
    items.sort((a, b) => a.sequence_number - b.sequence_number);
    return items.map(_fromRow);
  } catch (_e) {
    return [];
  }
}

/** Read EVERY cached message for a conversation. Used by the conv
 *  open hydrate path so scroll-up cache hits can land before the
 *  user even moves the wheel. Capped at PER_CONV_SOFT_CAP.
 */
export async function getAll(convId) {
  if (!convId) return [];
  const db = await _open(); if (!db) return [];
  try {
    return await new Promise((resolve) => {
      const store = _tx(db, "readonly");
      const range = IDBKeyRange.bound(
        [convId, -Infinity], [convId, Infinity],
      );
      const out = [];
      const req = store.openCursor(range);
      req.onsuccess = (ev) => {
        const cursor = ev.target.result;
        if (!cursor) { resolve(out); return; }
        out.push(cursor.value);
        cursor.continue();
      };
      req.onerror = () => resolve([]);
    }).then((items) =>
      items
        .sort((a, b) => a.sequence_number - b.sequence_number)
        .slice(-PER_CONV_SOFT_CAP)
        .map(_fromRow),
    );
  } catch (_e) {
    return [];
  }
}

/** Drop every cached message — called on logout. */
export async function clearAll() {
  const db = await _open(); if (!db) return;
  try {
    await new Promise((resolve) => {
      const store = _tx(db, "readwrite");
      const req = store.clear();
      req.onsuccess = resolve;
      req.onerror = resolve;
    });
  } catch (_e) { /* swallow */ }
}

/** Sweep messages older than ``MAX_AGE_MS``. Run once at startup. */
export async function sweepOld() {
  const db = await _open(); if (!db) return;
  const cutoff = Date.now() - MAX_AGE_MS;
  try {
    await new Promise((resolve) => {
      const store = _tx(db, "readwrite");
      const req = store.openCursor();
      req.onsuccess = (ev) => {
        const cursor = ev.target.result;
        if (!cursor) { resolve(); return; }
        const row = cursor.value;
        const ts = row.server_timestamp ? Date.parse(row.server_timestamp) : 0;
        // ``cached_at`` is the local-cache timestamp; ``server_timestamp``
        // is when the message was created. Drop a row when BOTH are
        // older than the cutoff (so a recent re-cache of an old msg
        // still survives 30 days).
        if ((!ts || ts < cutoff) && (row.cached_at || 0) < cutoff) {
          cursor.delete();
        }
        cursor.continue();
      };
      req.onerror = resolve;
    });
  } catch (_e) { /* swallow */ }
}

// ── Internals ───────────────────────────────────────────────────

function _fromRow(row) {
  if (!row) return null;
  return {
    message_id: row.message_id,
    conversation_id: row.conversation_id,
    sequence_number: row.sequence_number,
    sender: row.sender,
    content_type: row.content_type,
    content: row.content,
    server_timestamp: row.server_timestamp,
    edited: !!row.edited,
    recalled: !!row.recalled,
    event_type: row.event_type,
    event_subject: row.event_subject,
    event_subject_label: row.event_subject_label || null,
    mentions: row.mentions || [],
  };
}

async function _enforceSoftCap(convId) {
  const db = await _open(); if (!db) return;
  try {
    await new Promise((resolve) => {
      const store = _tx(db, "readwrite");
      const range = IDBKeyRange.bound(
        [convId, -Infinity], [convId, Infinity],
      );
      // Count first; only walk to delete if we're over the cap.
      const cReq = store.count(range);
      cReq.onsuccess = () => {
        const total = cReq.result || 0;
        if (total <= PER_CONV_SOFT_CAP) { resolve(); return; }
        const toDelete = total - PER_CONV_SOFT_CAP;
        let deleted = 0;
        const dReq = store.openCursor(range);
        dReq.onsuccess = (ev) => {
          const cursor = ev.target.result;
          if (!cursor || deleted >= toDelete) { resolve(); return; }
          cursor.delete();
          deleted += 1;
          cursor.continue();
        };
        dReq.onerror = resolve;
      };
      cReq.onerror = resolve;
    });
  } catch (_e) { /* swallow */ }
}

// Kick a sweep on first import so any 30+-day-old rows get cleaned
// up without waiting for an explicit caller. Catches the edge case
// where a user opens the app after a long pause.
sweepOld().catch(() => {});
